type PlainObject = Record<string, unknown>;

const isPlainObject = (value: unknown): value is PlainObject =>
  typeof value === 'object' &&
  value !== null &&
  !Array.isArray(value) &&
  Object.getPrototypeOf(value) === Object.prototype;

const cloneValue = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(cloneValue);
  }

  if (isPlainObject(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, nestedValue]) => [
        key,
        cloneValue(nestedValue),
      ]),
    );
  }

  return value;
};

/**
 * Recursively merges plain objects. Arrays from the override replace base arrays.
 */
export const deepMerge = <T>(base: T, override: unknown): T => {
  if (!isPlainObject(base) || !isPlainObject(override)) {
    return cloneValue(override) as T;
  }

  const merged = cloneValue(base) as PlainObject;

  Object.entries(override).forEach(([key, overrideValue]) => {
    merged[key] = deepMerge(merged[key], overrideValue);
  });

  return merged as T;
};
